'use client';
import { useMemo, type ComponentProps } from 'react';
import { useI18n } from '@locales/client';
import { GameSection } from '@/widgets/game-section';
import {
  HOME_GAME_SECTION_IDS,
  type HomeGame,
  type HomeGamesResponse,
} from '@/widgets/game-sections/ui/home-games-types';

type HomeSectionId = (typeof HOME_GAME_SECTION_IDS)[number];
type GameSectionProps = ComponentProps<typeof GameSection>;
type Translate = ReturnType<typeof useI18n>;

interface RenderHomeGameSectionsProps {
  sections?: HomeGamesResponse;
  openNotAllowedModal?: () => void;
  setCurrentGame: (game: HomeGame) => void;
}

const getSectionTitle = (sectionId: HomeSectionId, t: Translate) => {
  switch (sectionId) {
    case 'bonus':
      return t('common.filter.category.bonus');
    case 'preferred':
      return t('favorite_games');
    case 'recommended':
      return t('recommended_games');
    case 'popular':
      return t('common.filter.category.popular');
  }
};

const getSectionViewCount = (
  sectionId: HomeSectionId,
  games: HomeGame[],
  hasBonusSection: boolean
) => {
  switch (sectionId) {
    case 'popular':
    case 'bonus':
      return games.length > 6;
    case 'preferred':
      return !hasBonusSection;
    case 'recommended':
      return false;
  }
};

const RenderHomeGameSections = ({
  sections,
  openNotAllowedModal,
  setCurrentGame,
}: RenderHomeGameSectionsProps) => {
  const t = useI18n();

  const { normalizedSections, hasBonusSection } = useMemo(() => {
    const result: Array<{ id: HomeSectionId; games: HomeGame[] }> = [];
    let hasBonus = false;

    for (const id of HOME_GAME_SECTION_IDS) {
      const games = (sections?.[id] ?? []).filter(Boolean);
      if (games.length === 0) continue;

      result.push({ id, games });
      if (id === 'bonus') hasBonus = true;
    }

    return { normalizedSections: result, hasBonusSection: hasBonus };
  }, [sections]);

  return (
    <>
      {normalizedSections.map(({ id, games }) => (
        <GameSection
          key={id}
          isGames={false}
          hasScroll={true}
          rows={games.length < 4 ? 1 : 2}
          title={getSectionTitle(id, t)}
          openNotAllowedModal={openNotAllowedModal}
          games={games as unknown as GameSectionProps['games']}
          onGameClick={
            setCurrentGame as unknown as GameSectionProps['onGameClick']
          }
          viewCount={getSectionViewCount(id, games, hasBonusSection)}
          totalGamesCount={sections?.totalGamesCount}
        />
      ))}
    </>
  );
};

export { RenderHomeGameSections };
